import React, { useRef, useState, useEffect } from 'react';
import { ARController } from 'artoolkit5-js';
import Logo from 'svg/logo';

// Options for capturing user media
const CAPTURE_OPTIONS = {
  audio: false,
  video: {
    frameRate: 60,
    facingMode: 'environment',
    colorTemperature: 1000
  }
};
const videoScale = 0.94;

/**
 * Check if Chrome
 */
const checkIsChrome = () => {
  return ((navigator.userAgent.toLowerCase().indexOf('chrome') > -1) &&(navigator.vendor.toLowerCase().indexOf("google") > -1));
};

/**
 * Request interval function
 * @param  {Function} fn    [function to call]
 * @param  {number}   delay [delay for interval]
 *
 * https://css-tricks.com/snippets/javascript/replacements-setinterval-using-requestanimationframe/
 */
const requestInterval = (fn, delay) => {
  const requestAnimFrame = (() => {
    return window.requestAnimationFrame;
  })();
  let start = new Date().getTime();
  let handle = {};
  const loop = () => {
    handle.value = requestAnimFrame(loop);
    let current = new Date().getTime(),
    delta = current - start;
    if (delta >= handle.delay) {
      fn.call();
      start = new Date().getTime();
    }
  }
  handle.delay = delay;
  handle.value = requestAnimFrame(loop);
  return handle;
};

/**
 * Cancel the request interval function
 */
const cancelRequestInterval = (handle) => {
  window.cancelAnimationFrame(handle.value);
};

const ARView = (props) => {
  // Our callback function
  const cb = props.dataCallback;
  // States
  const [noAr, setNoAr] = useState(false);
  const [err, setErr] = useState('');
  const [waitingForStream, setWaitingForStream] = useState(false);
  const [isChrome, setIsChrome] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  // References to video and canvas and interval
  const mediaStream = useRef(null);
  const resetting = useRef(false);
  const videoRef = useRef();
  const canvasRef = useRef();
  const ctx = useRef(null);
  const dimensions = useRef({width: 0, height: 0});
  const rafIntervalFn = useRef(null);
  const isCurrentlyDownloading = useRef(false);

  /**
   * Handler for the onCanPlay event on the video
   */
  const handleCanPlay = () => {
    // If we already have an interval function running,
    // cancel it bc we're going to do a new one
    if (rafIntervalFn.current) {
      cancelRequestInterval(rafIntervalFn.current);
    }
    videoRef.current.play();
    console.log('settings and capabilities', mediaStream.current.getVideoTracks()[0].getSettings(), mediaStream.current.getVideoTracks()[0].getCapabilities());
    const options = {};
    ctx.current = canvasRef.current.getContext('2d');
    ARController.initWithImage(videoRef.current, './data/camera_para.dat', options).then((arController) => {
      setWaitingForStream(false);
      props.setHasVideo(true);
      arController.setPatternDetectionMode(arController.artoolkit.AR_MATRIX_CODE_DETECTION);
      arController.setMatrixCodeType(arController.artoolkit.AR_MATRIX_CODE_3x3);
      // We're ready to play video, so we are done resetting
      resetting.current = false;
      setIsResetting(false);
      rafIntervalFn.current = requestInterval(() => {
        if (!resetting.current) {
          arController.process(videoRef.current);
          let markers = []
          let len = arController.getMarkerNum();
          for (var i = 0; i < len; i++) {
            var m = arController.getMarker(i);
            //FIX -- some weirdness with val/reference going on here so we JSON it to kill it
            markers.push(JSON.parse(JSON.stringify(m)));
          }
          if (canvasRef.current) {
            ctx.current.clearRect(0, 0, dimensions.current.width, dimensions.current.height);
            ctx.current.drawImage(videoRef.current, 0, 0);
            cb(markers,
              ctx.current.getImageData(0, 0, dimensions.current.width, dimensions.current.height),
              dimensions.current.width,
              dimensions.current.height);
          }
        }
      }, 66);
    }).catch(err => {
      props.setHasVideo(false);
      if (err.toString().indexOf('RangeError') !== -1) {
        setErr('Oh no! This is a pretty memory intensive application, and it looks like your phone stopped allowing it. Please refresh to try again.');
      } else {
        setErr(`Oh no! Something went wrong. It's possible your device does not support the technology required to run this experience. You can refresh to try again.`);
      }
      setNoAr(true);
    });
  }

  /**
   * Set the stream
   */
  const setStream = () => {
    const setNewStream = async () => {
      try {
        console.log('WE ARE TRYING TO SET STREAM');
        resetting.current = true;
        setIsResetting(true);
        setNoAr(false);
        dimensions.current.width = document.documentElement.clientWidth * videoScale;
        dimensions.current.height = document.documentElement.clientHeight * videoScale;
        CAPTURE_OPTIONS.video.width = dimensions.current.width;
        CAPTURE_OPTIONS.video.height = dimensions.current.height;
        videoRef.current.width = dimensions.current.width;
        videoRef.current.height = dimensions.current.width;
        canvasRef.current.width = dimensions.current.width;
        canvasRef.current.height = dimensions.current.height;
        if (mediaStream.current) {
          mediaStream.current.getTracks().forEach(track => {
            track.stop();
          });
          videoRef.current.srcObject = null;
          mediaStream.current = null;
        }
        const stream = await navigator.mediaDevices.getUserMedia(CAPTURE_OPTIONS);
        mediaStream.current = stream;
        // Check if we have media stream,
        // which we set in our useEffect
        if (mediaStream.current && videoRef.current && !videoRef.current.srcObject) {
          videoRef.current.srcObject = mediaStream.current;
        }
        // If we already have an interval function running,
        // cancel it bc we're going to do a new one
        if (rafIntervalFn.current) {
          cancelRequestInterval(rafIntervalFn.current);
        }
      } catch (err) {
        if (err.toString().indexOf('NotAllowedError') !== -1) {
          setErr(`Oh no! You have to allow camera access to use the application. Please refresh and allow camera use.`);
        } else {
          setErr(`Oh no! Something went wrong. It's possible your device does not support the technology required to run this experience. You can refresh to try again.`);
        }
        setNoAr(true);
      }
    }
    if (!isCurrentlyDownloading.current &&
      !resetting.current) {
      setNewStream();
    }
  };

  /**
   * Degrade the experience
   * when we get a call to degrade we want to lower the
   * frequency of the AR callback function
   */
  useEffect(() => {
    if (props.degrade) {
      rafIntervalFn.current.delay = 500;
    }
  }, [props.degrade]);

  /**
   * Instantiation of stream
   */
  useEffect(() => {
    setIsChrome(checkIsChrome());
    const enableStream = () => {
      setStream();
      setWaitingForStream(true);
    }
    if (!mediaStream.current) {
      enableStream();
    } else {
      return function cleanup() {
        mediaStream.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    } // eslint-disable-next-line
  }, []);

  /**
   * Resize listener
   */
  useEffect(() => {
    window.addEventListener('resize', setStream);
    return function cleanup() {
      window.removeEventListener('resize', setStream);
    } // eslint-disable-next-line
  }, []);

  /**
   * Download listener
   */
  useEffect(() => {
    isCurrentlyDownloading.current = props.showDownload;
    // If we're done downloading, reset the stream
    if (!props.showDownload) {
      setStream();
    }
  }, [props.showDownload]);

  return (
    <section className='ARView'>
      {isResetting && (
        <div className='ARView__resetting'></div>
      )}
      {waitingForStream && (
        <div className='ARView__waiting'>
          <p>Loading camera data<span className='one'>.</span><span className='two'>.</span><span className='three'>.</span></p>
          {isChrome && (
            <p className='ARView__waiting-small'>Make sure you don't have another tab that is currently using the camera</p>
          )}
        </div>
      )}
      <video
        id='video'
        className='ARView__video'
        ref={videoRef}
        onCanPlay={handleCanPlay}
        autoPlay
        playsInline
        muted
      />
      <canvas ref={canvasRef} className='ARView__canvas-ref' />
      {noAr && (
        <div className='GenericAlert'>
          <Logo className='Intro__logo' />
          <h1 className='GenericAlert__title'>Sorry</h1>
          <section className='GenericAlert__description'>
            <p>{err}</p>
          </section>
        </div>
      )}
    </section>
  );
}

export default ARView;
